import {
  AHWidget,
  AHWidgetTitle,
  useScaleFactor,
  AHWidgetLoading,
  Box,
  useWidgetOptions,
} from "arkh";
import {
  FlatList,
  NativeScrollEvent,
  NativeSyntheticEvent,
  View,
} from "react-native";
import React, { useCallback, useMemo } from "react";
import { RoutineOverviewListItem } from "./WidgetRoutineOverviewListItem";
import { useRoutineOverviewListItemHeight } from "./useRoutineOverviewListItemHeight";

import { BaseWidgetProps } from "../BaseProps";
import { sortTeamDividedRoutineOccasion } from "./sortTeamDividedRoutineOccasion";
import { useSetScrollFocusMutation } from "../Focus/useSetFocusScroll";

import { graphql, useFragment, useLazyLoadQuery } from "react-relay";
import { WidgetRoutineOverview_widgetRoutineOverview$key } from "./__generated__/WidgetRoutineOverview_widgetRoutineOverview.graphql";
import { useEvent } from "../useEvent";
import { WidgetRoutineOverviewQuery } from "./__generated__/WidgetRoutineOverviewQuery.graphql";
import { WidgetRoutineOverviewDataComponent_widgetRoutineOverview$key } from "./__generated__/WidgetRoutineOverviewDataComponent_widgetRoutineOverview.graphql";
import { usePollFetchKey } from "../usePollFetchKey";

export const WidgetRoutineOverview = (
  props: {
    widget: WidgetRoutineOverview_widgetRoutineOverview$key;
  } & BaseWidgetProps
) => {
  const widget = useFragment(
    graphql`
      fragment WidgetRoutineOverview_widgetRoutineOverview on WidgetRoutineOverview {
        id
        title
      }
    `,
    props.widget
  );

  const fk = usePollFetchKey(props.interactive === false ? null : 60000);

  const data = useLazyLoadQuery<WidgetRoutineOverviewQuery>(
    graphql`
      query WidgetRoutineOverviewQuery($widgetId: ID!) {
        widgetRoutineOverview(id: $widgetId) {
          id
          title
          ...WidgetRoutineOverviewDataComponent_widgetRoutineOverview
        }
      }
    `,
    {
      widgetId: widget.id,
    },
    {
      fetchKey: fk,
      fetchPolicy: "store-and-network",
    }
  );

  if (data?.widgetRoutineOverview) {
    return (
      <WidgetRoutineOverviewDataComponent
        {...props}
        widget={data.widgetRoutineOverview}
      />
    );
  }

  return <AHWidgetLoading title={widget.title} />;
};

export const WidgetRoutineOverviewDataComponent = ({
  widget: widgetRef,
  interactive,
  onEvent,
  storecastId,
  mode,
  isFocused,
}: {
  widget: WidgetRoutineOverviewDataComponent_widgetRoutineOverview$key;
} & BaseWidgetProps) => {
  const widget = useFragment(
    graphql`
      fragment WidgetRoutineOverviewDataComponent_widgetRoutineOverview on WidgetRoutineOverview {
        id
        title
        routines {
          id
          ...WidgetRoutineOverviewListItem_routine
          inProgressOccasion {
            expireAt
            startAt
            status(calculateNoTasks: true, calculateNotCompletedStarted: true)
            ...WidgetRoutineOverviewListItem_routineOccasion
          }
        }
      }
    `,
    widgetRef
  );

  type Item = (typeof widget.routines)[number];

  const items = useMemo(
    () => sortTeamDividedRoutineOccasion(widget.routines),
    [widget.routines]
  );
  const onPressRow = useEvent((routineId: string, sequenceNumber: number) => {
    if (interactive === false) {
      return;
    }

    onEvent?.({
      kind: "RoutineOccasion",
      routineId,
      sequenceNumber,
      widgetId: widget.id,
    });
  });
  const renderRow = useCallback(
    ({ item }: { item: Item }) => {
      if (!item.inProgressOccasion) {
        return null;
      }
      return (
        <RoutineOverviewListItem
          key={item.id}
          onPress={onPressRow}
          routine={item}
          routineOccasion={item.inProgressOccasion}
        />
      );
    },
    [onPressRow]
  );

  const sf = useScaleFactor();

  const listItemHeight = useRoutineOverviewListItemHeight();
  const getItemLayout = useCallback(
    (_d: unknown, index: number) => ({
      length: listItemHeight,
      offset: listItemHeight * index,
      index,
    }),
    [listItemHeight]
  );
  const setScroll = useSetScrollFocusMutation(storecastId, widget.id);
  const { background } = useWidgetOptions();

  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const index = Math.floor(
        (event.nativeEvent.targetContentOffset
          ? event.nativeEvent.targetContentOffset.y
          : event.nativeEvent.contentOffset.y) / listItemHeight
      );
      const item = items[index];
      if (item) {
        setScroll(item.id);
      }
    },
    [items, listItemHeight, setScroll]
  );

  if (interactive === false) {
    return (
      <AHWidget backgroundColor={background}>
        <AHWidgetTitle>{widget.title}</AHWidgetTitle>
        <View>{items.slice(0, 10).map((item) => renderRow({ item }))}</View>
      </AHWidget>
    );
  }

  return (
    <Box color="surface" style={{ flex: 1, backgroundColor: background }}>
      <AHWidgetTitle>{widget.title}</AHWidgetTitle>
      <FlatList
        initialNumToRender={5}
        scrollEnabled={interactive !== false}
        showsVerticalScrollIndicator={false}
        data={items}
        scrollEventThrottle={500}
        onScroll={isFocused ? onScroll : undefined}
        getItemLayout={getItemLayout}
        renderItem={renderRow}
      />
    </Box>
  );
};
